import { motion, AnimatePresence } from 'motion/react';
import { useState, useRef, useEffect } from 'react';

interface StandardTooltipProps {
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  children: React.ReactNode;
}

export function StandardTooltip({ 
  content, 
  position = 'bottom',
  children,
}: StandardTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const padding = 16; // Safe margin from screen edges
      
      let top = 0;
      let left = 0;

      // Calculate base position
      switch (position) {
        case 'bottom':
          top = triggerRect.bottom + 8;
          left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
          break;
        case 'top':
          top = triggerRect.top - tooltipRect.height - 8;
          left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
          break;
        case 'left':
          top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
          left = triggerRect.left - tooltipRect.width - 8;
          break;
        case 'right':
          top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
          left = triggerRect.right + 8;
          break;
      }

      // Adjust for screen boundaries
      if (left < padding) left = padding;
      if (left + tooltipRect.width > window.innerWidth - padding) {
        left = window.innerWidth - tooltipRect.width - padding;
      }
      if (top < padding) top = padding;
      if (top + tooltipRect.height > window.innerHeight - padding) {
        top = window.innerHeight - tooltipRect.height - padding;
      }

      setTooltipPosition({ top, left });
    }
  }, [isOpen, position]);

  return (
    <>
      <div 
        ref={triggerRef}
        onClick={handleToggle}
        className="relative inline-flex cursor-pointer"
        style={{
          minWidth: '40px',
          minHeight: '40px',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
      
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop to close on tap outside */}
            <motion.div
              className="fixed inset-0 z-40"
              onClick={handleClose}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ backgroundColor: 'transparent' }}
            />

            {/* Tooltip */}
            <motion.div
              ref={tooltipRef}
              className="fixed z-50"
              style={{
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                backgroundColor: 'var(--kojo-blue)',
                borderRadius: '14px',
                padding: '12px 16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                maxWidth: '280px',
              }}
              initial={{ opacity: 0, scale: 0.95, y: position === 'bottom' ? -8 : 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: position === 'bottom' ? -8 : 8 }}
              transition={{ duration: 0.12, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <p 
                className="text-white"
                style={{
                  fontSize: 'var(--text-sm)',
                  lineHeight: '1.5',
                  textAlign: 'left',
                  fontWeight: 'var(--font-weight-normal)',
                }}
              >
                {content}
              </p>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
